import React, { useEffect, useMemo } from "react"

import { ButtonElement } from "@front-shared/elements/button.element"
import { ConfirmationHelper } from "@front-shared/helpers/confirmation.helper"

import { ButtonDropdownElement } from "../../../elements/buttonDropdown.element"
import { RuntimeHelper } from "../../../helpers/runtime.helper"
import { useAppDispatch } from "../../../store/appDispatch.hook"
import { useAppSelector } from "../../../store/selectors/app.selector"
import { networkStatusSelector } from "../../../store/selectors/networkStatus.selector"
import { runtimeSelector } from "../../../store/selectors/runtime.selector"
import { selectedPracticeRoleSelector } from "../../../store/selectors/selectedPracticeRole.selector"
import {
    networkStatusGetVersions,
    networkStatusSetActiveVersion,
} from "../../../store/thunks/networkStatus.thunks"

import chevronDownIcon from "@front-shared/assets/images/icons/chevron-down-dark.svg"

export const ActiveVersionSelectorComponent: React.FunctionComponent<
    IActiveVersionSelectorComponentProps
> = props => {
    const dispatch = useAppDispatch()

    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)
    const runtimeState = useAppSelector(runtimeSelector)
    const networkStatusState = useAppSelector(networkStatusSelector)

    const isVersionsLoading = useMemo<boolean>(() => {
        return RuntimeHelper.isNetworkStatusGetVersionsLoading()
    }, [runtimeState.isLoading])

    const isSetActiveLoading = useMemo<boolean>(() => {
        return RuntimeHelper.isNetworkStatusSetActiveVersionLoading()
    }, [runtimeState.isLoading])

    // Active version that is assigned to practice right now
    const activePracticeVersion = useMemo<number | null>(() => {
        return (
            selectedPracticeRole?.practice.networkStatusRulesVersion?.version ??
            null
        )
    }, [selectedPracticeRole?.practice.networkStatusRulesVersion?.version])

    // Load available versions list
    useEffect(() => {
        if (!selectedPracticeRole) {
            return
        }

        dispatch(
            networkStatusGetVersions({
                practice: selectedPracticeRole.practice,
                payload: undefined,
            }),
        )
    }, [selectedPracticeRole?.practice.id])

    // Default selected version to active when versions have loaded
    useEffect(() => {
        if (
            typeof activePracticeVersion === "number" &&
            networkStatusState.versions.some(
                v => v.version === activePracticeVersion,
            )
        ) {
            props.onRulesVersionSelected(activePracticeVersion)
        }
    }, [activePracticeVersion, networkStatusState.versions])

    const onSetActiveVersion = () => {
        if (
            !selectedPracticeRole?.practice ||
            typeof props.selectedRulesVersion !== "number"
        ) {
            return
        }

        const versionRecord = networkStatusState.versions.find(
            v => v.version === props.selectedRulesVersion,
        )

        if (!versionRecord) {
            return
        }

        ConfirmationHelper.enableConfirmation(
            () => {
                dispatch(
                    networkStatusSetActiveVersion({
                        practice: selectedPracticeRole.practice,
                        payload: {
                            versionId: versionRecord.id,
                        },
                    }),
                )
            },
            `Set version ${props.selectedRulesVersion} as active?`,
            `This will affect all checks that are running in the practice and may cause some of them to produce incorrect alerts if there are breaking changes in the new version.`,
        )
    }

    return (
        <div
            className={`
                flex items-center flex-shrink-0 gap-2
                ${props.className}
            `}
        >
            <ButtonDropdownElement
                buttonLabel={
                    typeof props.selectedRulesVersion === "number"
                        ? `Version ${props.selectedRulesVersion}`
                        : "Version"
                }
                buttonType="default"
                buttonIcon={chevronDownIcon}
                buttonIsRightIcon
                buttonIsDisabled={
                    !networkStatusState.versions.length || isVersionsLoading
                }
                buttonIsLoading={isVersionsLoading}
                dropdownClassName="min-w-[160px]"
                buttonClassName="
                    rounded-[22px] px-[16px] py-[13px]
                    bg-brand-warmLight border border-solid
                    border-brand-primary hover:bg-brand-lilacLight
                "
                options={networkStatusState.versions.map(v => ({
                    label: `Version ${v.version}`,
                    isSelected: props.selectedRulesVersion === v.version,
                    onClick: () => {
                        props.onRulesVersionSelected(v.version)
                    },
                }))}
            />

            <ButtonElement
                label="Set as active"
                buttonClassName="
                    flex items-center justify-center
                    rounded-[22px] px-[16px] py-[13px] bg-brand-warmLight
                    border border-solid border-brand-primary
                    hover:bg-brand-lilacLight disabled:opacity-50 disabled:cursor-not-allowed
                "
                htmlType="button"
                type="default"
                isLoading={isSetActiveLoading}
                disabled={
                    isSetActiveLoading ||
                    isVersionsLoading ||
                    !networkStatusState.versions.length ||
                    activePracticeVersion === props.selectedRulesVersion
                }
                onClick={onSetActiveVersion}
            />
        </div>
    )
}

interface IActiveVersionSelectorComponentProps {
    className?: string
    selectedRulesVersion: number | null
    onRulesVersionSelected: (version: number) => void
}
